export const PRIMARY_ADMISSION_EVENT_SLUG = 'timed-tickets'
export const EXHIBITION_SLUG_POSTFIX = 'admission'
export const EVENTS_SLUG_POSTFIX = 'events'
export const EVENTS_CHECKOUT_BASE_PATH = 'purchase'

export const MAIN_NAV: Array<[text: string, url: string]> = [
  ['Visit', 'https://meowwolf.com/visit'],
  ['Events', 'https://tickets.meowwolf.com/events/'], // TODO: change this
  ['Shop', 'https://shop.meowwolf.com/'],
  ['About', 'https://meowwolf.com/about'],
]

export const DOMAINS = {
  prod: 'meowwolf.com',
  preview: 'meowwolf.dev',
}

export const DEPLOYMENT_URLS = {
  prod: `https://tickets.${DOMAINS.prod}`,
  staging: `https://tickets-staging.${DOMAINS.preview}`,
  playground: `https://tickets-playground.${DOMAINS.preview}`,
  prodPlayground: `https://tickets-prod-playground.${DOMAINS.preview}`,
  demo: `https://tickets-demo.${DOMAINS.preview}`,
  preProd: `https://tickets.${DOMAINS.preview}`,
}

/**
 * @enum {string}
 */
export enum STEP_SLUG {
  Tickets = 'ticket-type',
  AddOns = 'add-ons',
  CustomerInfo = 'customer-info',
  Checkout = 'checkout',
  Confirmation = 'confirmation',
}

export type StepId = '/' | 'ticket-type' | 'add-ons' | 'customer-info' | 'checkout' | 'confirmation'

/**
 * add
 * - isDirty
 * - isActive (?) // handled a level up by the state machine (currentStep)
 * - isLockable // handle in the machine, not on the step
 * - nextStep
 * - previousStep
 */
export type StepsMeta = {
  route: string // url
  id: StepId
  name: string // display name
  enabled: boolean // display step or ignore it
  cartRequired: boolean
  cartAllowed: boolean
}

const toSlug = (str: string): string => str.toLowerCase()
  .replace(/\s+/g, '-')

/**
 * Generates an immutable array of step metadata for event-based navigation.
 *
 * @param {string} basePath - The base path for constructing step routes.
 * @returns {ReadonlyArray<StepsMeta>} A readonly array of step metadata, where each object contains:
 * - `id`: A unique identifier for the step.
 * - `route`: The URL path for the step, constructed using the `basePath` and predefined constants.
 * - `name`: A human-readable name for the step.
 * - `enabled`: A boolean indicating whether the step is active or not.
 */
const eventsStepsMeta = (basePath: string): readonly StepsMeta[] => ([
  {
    id: 'customer-info',
    route: `/${basePath}/${EVENTS_CHECKOUT_BASE_PATH}/${STEP_SLUG.CustomerInfo}/`,
    name: 'Customer Info',
    enabled: true,
    cartRequired: true,
    cartAllowed: true,
  },
  {
    id: 'checkout',
    route: `/${basePath}/${EVENTS_CHECKOUT_BASE_PATH}/${STEP_SLUG.Checkout}/`,
    name: 'Checkout',
    enabled: true,
    cartRequired: true,
    cartAllowed: true,
  },
])

/**
 * Generates a list of metadata for the admissions steps in a process.
 *
 * @param {string} basePath - The base path for constructing routes for each step.
 * @param {Transformed.Event | null} addOnsEventData - Optional event data to customize the "Add-Ons" step.
 * @returns {ReadonlyArray<StepsMeta>} - A readonly array of step metadata, each containing the id, route, name, and enabled status of a step.
 */
const admissionsStepsMeta = (
  basePath: string,
  addOnsEventData: Transformed.Event | null,
): readonly StepsMeta[] => ([
  {
    id: '/',
    route: `/${basePath}/`,
    name: 'Date & Time',
    enabled: true,
    cartRequired: false,
    cartAllowed: false,
  },
  {
    id: 'ticket-type',
    route: `/${basePath}/${STEP_SLUG.Tickets}/`,
    name: 'Tickets',
    enabled: true,
    cartRequired: false,
    cartAllowed: true,
  },
  {
    id: 'add-ons',
    route: `/${basePath}/${toSlug(addOnsEventData?.title || STEP_SLUG.AddOns)}/`,
    name: addOnsEventData?.title || 'Add-Ons',
    enabled: !!addOnsEventData,
    cartRequired: true,
    cartAllowed: true,
  },
  {
    id: 'customer-info',
    route: `/${basePath}/${STEP_SLUG.CustomerInfo}/`,
    name: 'Customer Info',
    enabled: true,
    cartRequired: true,
    cartAllowed: true,
  },
  {
    id: 'checkout',
    route: `/${basePath}/${STEP_SLUG.Checkout}/`,
    name: 'Checkout',
    enabled: true,
    cartRequired: true,
    cartAllowed: true,
  },
])

/**
 * Convert the above, typesafe object to a map for us to use
 * in the app
 * @param sitePath the site path, e.g. `santa-fe/admission`
 * @param eventNameOrId
 * @param addOnsEventData Data relevant to any add-ons
 * @param isEvents
 * @returns Map<number, StepsMeta>
 */
export const transformStepsMeta = (
  sitePath: string,
  eventNameOrId: string | undefined,
  addOnsEventData: Nullable<Transformed.Event>,
  isEvents: boolean,
): readonly StepsMeta[] => {
  const stepsMeta = isEvents ? eventsStepsMeta : admissionsStepsMeta
  const basePath = eventNameOrId ? `${sitePath}/${eventNameOrId}` : sitePath
  return stepsMeta(basePath, addOnsEventData).filter((step) => step.enabled)
}

export const PORTAL_SELECTORS = {
  footer: 'portal-footer',
  mobileOrderFooter: 'portal-mobile-order-footer',
  mobileOrderSummary: 'portal-mobile-order-summary',
  mobileTimer: 'portal-mobile-timer',
  modal: 'portal-modal',
  loadingBlob: 'portal-loading-blob',
  skipToMain: 'portal-skip-to-main',
  purchase: 'portal-purchase',
  lifeTimePass: 'portal-life-time-pass',
  ticketsButton: 'portal-tickets-button',
  cartButton: 'portal-cart-button',
  authMenu: 'portal-auth-menu',
}

/** Cookie-gated preview routes, checked in proxy.ts. */
export const PREVIEW_ROUTES = {
  // TODO: TICKT-2197 should be events/<seller>/preview-event
  EventsPreview: 'preview-event/events',
  AdmissionPreview: `preview/${EXHIBITION_SLUG_POSTFIX}`,
} as const

export const DAYPASS_URL_PARAM: AdmissionTypeUrlParam = 'daypass'

export const TIMED_TICKET_URL_PARAM: AdmissionTypeUrlParam = 'timed_ticket'
